import type { DataTableSort } from "@/components/shared/data-table/DataTable";
import type { InventoryScopeId } from "@/features/inventory/types";
import { setOptionalParam } from "@/lib/hooks/use-document-search-params";

export const INVENTORY_QUERY_KEYS = {
  page: "page",
  scope: "scope",
  search: "q",
  sortDirection: "dir",
  sortField: "sort",
} as const;

const MAX_PAGE = 10_000;

const SCOPE_IDS: readonly InventoryScopeId[] = ["in_stock", "out_on_sample", "sold", "all"];

/**
 * The slice an operator lands on. Stock on hand is the sellable set, so it is
 * what the page opens with; every other slice is one chip away and shareable
 * because the pick lives in the URL.
 */
export const DEFAULT_INVENTORY_SCOPE: InventoryScopeId = "in_stock";

/**
 * Columns the list may be ordered by.
 *
 * The sort field is handed straight to PostgREST `.order()`, so it is matched
 * against this list rather than trusted: a hand-edited URL cannot name a column
 * the table does not offer.
 */
export const INVENTORY_SORT_FIELDS = [
  "product_name",
  "sku",
  "purchase_order_uid",
  "quantity",
  "qty_in_stock",
  "qty_sampled",
  "qty_sold",
  "cost",
  "total_cost",
  "created_at",
] as const;

export type InventorySortField = (typeof INVENTORY_SORT_FIELDS)[number];

/** Order applied when the operator has not picked one: newest products first. */
export const DEFAULT_INVENTORY_SORT: DataTableSort = {
  direction: "desc",
  field: "created_at",
};

export interface InventoryPageFilters {
  page: number;
  scope: InventoryScopeId;
  search: string;
  /** `null` when no explicit sort is in the URL; the query falls back to the default. */
  sort: DataTableSort | null;
}

function isInventoryScopeId(value: string): value is InventoryScopeId {
  return (SCOPE_IDS as readonly string[]).includes(value);
}

function isInventorySortField(value: string): value is InventorySortField {
  return (INVENTORY_SORT_FIELDS as readonly string[]).includes(value);
}

function readSort(searchParams: URLSearchParams): DataTableSort | null {
  const field = searchParams.get(INVENTORY_QUERY_KEYS.sortField)?.trim() ?? "";
  if (!isInventorySortField(field)) {
    return null;
  }
  const direction = searchParams.get(INVENTORY_QUERY_KEYS.sortDirection);
  return { direction: direction === "desc" ? "desc" : "asc", field };
}

export function readInventoryFiltersFromSearchParams(
  searchParams: URLSearchParams,
): InventoryPageFilters {
  const search = searchParams.get(INVENTORY_QUERY_KEYS.search)?.trim() ?? "";

  const scopeRaw = searchParams.get(INVENTORY_QUERY_KEYS.scope)?.trim() ?? "";
  const scope = isInventoryScopeId(scopeRaw) ? scopeRaw : DEFAULT_INVENTORY_SCOPE;

  const pageRaw = searchParams.get(INVENTORY_QUERY_KEYS.page);
  const pageParsed = pageRaw ? Number.parseInt(pageRaw, 10) : 1;
  const page = Number.isFinite(pageParsed) && pageParsed > 0 ? Math.min(pageParsed, MAX_PAGE) : 1;

  return { page, scope, search, sort: readSort(searchParams) };
}

export function writeInventoryFiltersToSearchParams(
  previous: URLSearchParams,
  next: Partial<InventoryPageFilters>,
): URLSearchParams {
  const searchParams = new URLSearchParams(previous);

  if ("search" in next) {
    setOptionalParam(searchParams, INVENTORY_QUERY_KEYS.search, next.search);
  }

  if ("scope" in next && next.scope !== undefined) {
    if (next.scope === DEFAULT_INVENTORY_SCOPE) {
      searchParams.delete(INVENTORY_QUERY_KEYS.scope);
    } else {
      searchParams.set(INVENTORY_QUERY_KEYS.scope, next.scope);
    }
  }

  if ("sort" in next) {
    if (next.sort) {
      searchParams.set(INVENTORY_QUERY_KEYS.sortField, next.sort.field);
      searchParams.set(INVENTORY_QUERY_KEYS.sortDirection, next.sort.direction);
    } else {
      searchParams.delete(INVENTORY_QUERY_KEYS.sortField);
      searchParams.delete(INVENTORY_QUERY_KEYS.sortDirection);
    }
  }

  if ("page" in next && next.page !== undefined) {
    if (next.page <= 1) {
      searchParams.delete(INVENTORY_QUERY_KEYS.page);
    } else {
      searchParams.set(INVENTORY_QUERY_KEYS.page, String(next.page));
    }
  }

  return searchParams;
}
